/** Content blocks for one owner — a chapter's `content` or a math exercise's `prompt`. Only `text` and
 *  `image` can be added; a stored audio block is still listed, reorderable and removable. */
import { computed, toRaw } from 'vue'

import type { ComputedRef } from 'vue'
import type {
  AssignmentContentBlock,
  AssignmentContentOwner,
} from './store/interfaces'

import { useAssignmentEditorStore } from './store'

type AssignmentAuthorableBlockType = 'text' | 'image'

interface AssignmentContentBlockItem {
  index: number
  block: AssignmentContentBlock
  /** `v-for` key: stable for the block's lifetime, never shared by two blocks. */
  key: string
  isFirst: boolean
  isLast: boolean
}

interface AssignmentContentBlockListProps {
  owner: AssignmentContentOwner
}

interface UseAssignmentContentBlockListOptions {
  props: AssignmentContentBlockListProps
}

interface UseAssignmentContentBlockListReturn {
  refBlocks: ComputedRef<AssignmentContentBlockItem[]>
  refHasBlocks: ComputedRef<boolean>
  addBlock: (type: AssignmentAuthorableBlockType) => void
  removeBlock: (index: number) => void
  moveBlockUp: (index: number) => void
  moveBlockDown: (index: number) => void
}

/**
 * Blocks carry no id, so they are keyed by object identity — the store mutates in place, so a block
 * keeps its object through edits and reorders. An `index-type` key is not unique enough: the list is
 * reused across chapters, so chapter B's first image block would take over the component instance
 * (uploader state, in-flight upload) of chapter A's. Scalars and `null` (unvalidated `data`) have no
 * identity and fall back to position.
 */
const blockKeys = new WeakMap<object, string>()
let nextBlockKey = 0

const blockKey = (block: unknown, index: number): string => {
  if (typeof block !== 'object' || block === null) {
    return `${index}-${String(block)}`
  }
  const raw = toRaw(block)
  let key = blockKeys.get(raw)

  if (key === undefined) {
    nextBlockKey += 1
    key = `block-${nextBlockKey}`
    blockKeys.set(raw, key)
  }

  return key
}

export const useAssignmentContentBlockList = (
  { props }: UseAssignmentContentBlockListOptions,
): UseAssignmentContentBlockListReturn => {
  const store = useAssignmentEditorStore()

  // Stored `data` is unvalidated: the getter guards a list that is not an array.
  const refContent = computed<AssignmentContentBlock[]>(() => store.getContentOf(props.owner))

  const refBlocks = computed<AssignmentContentBlockItem[]>(() =>
    refContent.value.map((block, index) => ({
      index,
      block,
      key: blockKey(block, index),
      isFirst: index === 0,
      isLast: index === refContent.value.length - 1,
    })),
  )

  const refHasBlocks = computed<boolean>(() => refContent.value.length > 0)

  const addBlock = (type: AssignmentAuthorableBlockType): void => {
    store.addContentBlock(props.owner, type)
  }

  const removeBlock = (index: number): void => {
    store.removeContentBlock(props.owner, index)
  }

  const moveBlockUp = (index: number): void => {
    store.moveContentBlockUp(props.owner, index)
  }

  const moveBlockDown = (index: number): void => {
    store.moveContentBlockDown(props.owner, index)
  }

  return {
    refBlocks,
    refHasBlocks,
    addBlock,
    removeBlock,
    moveBlockUp,
    moveBlockDown,
  }
}
